<template>
  <div>
    <AppDialog
      class="time_off_request_setup_dialog"
      size="large"
      title="Time off request"
      :loading="loadingInitial"
      :show="showDialog"
      append-to-body
      @close="closeDialog"
    >
      <ValidationObserver ref="dialogObserver">
        <div class="main-info">
          <div
            v-if="partiallyPaid"
            class="main-fields-partial-paid"
          >
            <div class="field policy-field">
              <label class="control-label required">Policy</label>
              <div>{{ selectedPolicyName }}</div>
            </div>
            <div class="field">
              <label class="control-label required">Requested time off</label>
              <div>{{ requestedTimeOffMessage }}</div>
              <small class="time_zone tz_message">{{ tzMessage }}</small>
            </div>
            <div class="field">
              <label class="control-label required">Message</label>
              <div class="current-message">{{ message }}</div>
            </div>
            <div
              v-if="responseForDeny"
              class="field"
            >
              <label class="control-label required">Response for deny</label>
              <div class="current-message">{{ responseForDeny }}</div>
            </div>
            <div
              v-if="hasAttachment"
              class="field mb-0"
            >
              <label class="control-label required">Uploaded file</label>
              <div class="d-flex justify-content-start align-items-center">
                <div class="filename">
                  {{ attachment.metadata.filename }}
                </div>
                <a :href="attachmentUrl">
                  <i class="hi hi-export hi-12 d-flex align-items-center ml-5" />
                </a>
              </div>
            </div>
          </div>
          <div
            v-else
            class="main-fields"
          >
            <div class="member">
              <div
                v-if="showUserField"
                class="field user-field"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  mode="lazy"
                  rules="required"
                  name="member"
                >
                  <div :class="{ 'has-error': errors[0] }">
                    <label class="control-label required">Member*</label>
                    <AppLazySelect
                      ref="user-selector"
                      v-model="selectedMemberObject"
                      resource="member"
                      :fetcher="fetcher"
                      :organization-id="organizationId"
                      @input="memberChanged"
                    >
                      <template #item-leading="{ item }">
                        <AppAvatar
                          v-if="item.avatar"
                          :avatar="item.avatar"
                          size="speck"
                        ></AppAvatar>
                      </template>
                    </AppLazySelect>
                    <span class="help-block">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
              <div
                class="field policy-field"
                :class="{ 'policy-field-row': showUserField }"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  mode="lazy"
                  rules="required"
                  name="policy"
                >
                  <div :class="{ 'has-error': errors[0] }">
                    <label class="control-label required">Policy*</label>
                    <AppSelect
                      v-model="selectedPolicy"
                      :options="timeOffPoliciesListData"
                      :select2-options="{ multiple: false, width: '100%', placeholder: 'Time off policy' }"
                      @input="policyChanged"
                    />
                    <span class="help-block">{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
            </div>
            <div>
              <label class="control-label required">Date range</label>
              <div class="date-range-fields">
                <div class="date start-date">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="starts_at"
                    mode="lazy"
                    rules="required"
                  >
                    <div :class="{ 'has-error': errors[0] }">
                      <AppDatePicker
                        :key="hasValidationError ? 'has-error' : 'no-error'"
                        v-tooltip="nextPageDateTooltip"
                        :value="startsAt"
                        :max-date="getOneYearsFromNow"
                        :disabled="hasValidationError"
                        hide-today
                        :show-quick-pick="false"
                        type="daily"
                        @input="updateDates($event, 'startsAt')"
                      />
                      <span class="help-block">{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </div>
                <span class="range-separator">-</span>
                <div class="date stop-date">
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="stops_at"
                    mode="lazy"
                    rules="required"
                  >
                    <div :class="{ 'has-error': errors[0] }">
                      <AppDatePicker
                        :key="`${hasValidationError ? 'has-error' : 'no-error'}-${startsAt}`"
                        v-tooltip="nextPageDateTooltip"
                        :value="stopsAt"
                        :min-date="startsAt"
                        :max-date="getOneYearsFromNow"
                        hide-today
                        :disabled="hasValidationError"
                        :show-quick-pick="false"
                        type="daily"
                        @input="updateDates($event, 'stopsAt')"
                      />
                      <span class="help-block">{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
            </div>
            <div class="all-day">
              <div class="title">
                <label class="control-label required start-end-time">Start/end time</label>
                <div class="checkboxes">
                  <AppCheckbox
                    v-model="allDay"
                    identifier="allDay"
                    :indeterminate="false"
                    :disabled="false"
                    @input="updateAllDay"
                  >
                    <span>All day (24hs)</span>
                  </AppCheckbox>
                  <i
                    v-tooltip="{ placement: 'bottom' }"
                    title="The member will be out 24 hours since the beginning of the request."
                    class="hi hi-info-filled hi-12 hi-tooltip text-grey all-day-label ml-5"
                  />
                </div>
              </div>
              <div class="time-span">
                <div class="time start-time">
                  <AppTimeInput
                    :key="allDay ? 'has-tooltip' : 'no-tooltip'"
                    v-tooltip="
                      allDay ? { placement: 'bottom', title: 'To add start time uncheck the all day setting' } : {}
                    "
                    :disabled="allDay"
                    :value="startTime"
                    show-seconds="never"
                    @input="changeTime(startTime, $event, 'startTime')"
                  />
                </div>
                <span class="range-separator">-</span>
                <div class="time stop-time">
                  <AppTimeInput
                    :key="allDay ? 'has-tooltip' : 'no-tooltip'"
                    v-tooltip="
                      allDay ? { placement: 'bottom', title: 'To add end time uncheck the all day setting' } : {}
                    "
                    :disabled="allDay"
                    :value="stopTime"
                    show-seconds="never"
                    @input="changeTime(stopTime, $event, 'stopTime')"
                  />
                </div>
              </div>
              <div
                v-if="showOriginalTimeZone || showTzMessage"
                class="time_zone"
              >
                <small class="tz_message">{{ tzMessage }}</small>
              </div>
            </div>
            <div v-if="shiftOverlappingCount > 0">
              <div class="form-group shift-count-warning-wrapper small">
                <p class="shift-count-warning mb-0">
                  <i class="hi-important mr-5"></i>
                  {{ shiftOverlappingMessage }}
                </p>
              </div>
            </div>
          </div>
          <div>
            <div :class="{ 'balances-margin-top': !partiallyPaid }">
              <div
                v-if="partiallyPaid"
                class="field mb-20"
              >
                <label class="control-label required">Status</label>
                <div
                  class="time_off_request_status"
                  :class="statusSymClass"
                >
                  {{ status }}
                </div>
              </div>
              <TimeOffRequestBalance
                :member-avatar="userBalanceAvatar"
                :member-name="userBalanceName"
                :policy-name="selectedPolicyName"
                :current-balance="currentBalance"
                :hours-used="hoursUsed"
                :remaining-balance="remainingBalance"
                :pending-approval="pendingApproval"
                :show-next-year-balance="showNextYearBalance"
                :current-balance-next-year="currentBalanceNextYear"
                :holiday-hours="holidayHours"
                :hours-used-next-year="hoursUsedNextYear"
                :remaining-balance-next-year="remainingBalanceNextYear"
                :pending-approval-next-year="pendingApprovalNextYear"
                :holiday-hours-next-year="holidayHoursNextYear"
                :start-at-year="startAtYear"
                :stop-at-year="stopAtYear"
              />
              <div class="mt-10">
                <ValidationObserver ref="policyObserver">
                  <ValidationProvider
                    v-slot="{ errors }"
                    mode="eager"
                    name="policy"
                  >
                    <div
                      v-show="errors[0]"
                      :class="{ 'has-error': errors[0] }"
                    >
                      <span class="help-block">{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </ValidationObserver>
                <ValidationProvider
                  v-slot="{ errors }"
                  mode="eager"
                  name="amount_used"
                >
                  <div
                    v-show="errors[0]"
                    :class="{ 'has-error': errors[0] }"
                  >
                    <span class="help-block">Hours used {{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </div>
            </div>
            <div v-if="loadingBalance && !showDetails">
              <div class="loading alert alert-warning mb-10 mt-10">
                <i class="fa fa-spinner fa-spin"></i>
                Loading...
              </div>
            </div>
          </div>
        </div>
        <div v-if="partiallyPaid">
          <div class="form-group shift-count-warning-wrapper small">
            <p class="shift-count-warning mb-0">
              <i class="hi-warning text-orange mr-5"></i>
              {{ paidDaysMessage }}
            </p>
          </div>
        </div>
        <div class="day-off-rules">
          <div class="options">
            <div class="checkboxes">
              <div class="exclude-weekend">
                <AppCheckbox
                  v-model="excludeWeekends"
                  identifier="excludeWeekends"
                  :indeterminate="false"
                  :disabled="false"
                  @input="excludeWeekendsChanged"
                >
                  <span>Exclude weekends</span>
                </AppCheckbox>
              </div>
              <div class="exclude-holiday">
                <AppCheckbox
                  v-model="excludeHolidays"
                  identifier="excludeHolidays"
                  :indeterminate="false"
                  :disabled="false"
                  @input="excludeHolidaysChanged"
                >
                  <span>Exclude holidays</span>
                </AppCheckbox>
              </div>
            </div>
            <div
              v-if="showDetails && !partiallyPaid"
              class="details text-blue cursor-pointer"
              @click="hideDetails(false)"
            >
              <b>Hide details</b>
              <i class="fa fa-chevron-up"></i>
            </div>
            <div
              v-else-if="!showDetails && !partiallyPaid"
              class="details text-blue cursor-pointer"
              @click="hideDetails(true)"
            >
              <b>Show details</b>
              <i class="fa fa-chevron-down"></i>
            </div>
          </div>
        </div>
        <div
          v-show="showDetails"
          class="table-wrapper"
        >
          <div class="scrollable">
            <ValidationObserver ref="tableObserver">
              <AppTable
                v-if="selectedMember"
                ref="table"
                :loading="loadingBalance"
                :table-data="paginatedItems"
              >
                <AppTableColumn
                  v-slot="{ row }"
                  header="Days off"
                  name="days_off"
                >
                  <div class="day-off-row">
                    <span class="date">
                      {{ formatDate(row.date) }}
                    </span>
                    <span
                      v-if="row.holiday"
                      class="small holiday"
                    >
                      <i class="hi hi-gift hi-16 text-blue"></i>
                      <span class="text">
                        <b>{{ holidayAmountUsedHours(row) }}</b>
                        {{ holidayType(row) }}
                      </span>
                    </span>
                  </div>
                </AppTableColumn>
                <AppTableColumn
                  v-slot="{ row }"
                  header="Hours per day"
                  name="hours_used"
                  header-tooltip="Hours deducted from remaining time off balance and reflected on reports"
                  header-tooltip-type="info-filled"
                  cell-classes="column-all-day"
                  :header-tooltip-size="12"
                >
                  <div class="duration-row">
                    <ValidationObserver :ref="`custom-allocation-hours-used-${row.date}`">
                      <ValidationProvider
                        v-slot="{ errors }"
                        :name="`hours_used`"
                      >
                        <div :class="{ 'has-error': errors[0] }">
                          <AppDurationInput
                            :key="uniqueKey"
                            v-tooltip="{ placement: 'bottom' }"
                            :data-original-title="hoursPerDayTooltipText(row)"
                            :value="row.amount_used"
                            :disabled="row.disabled"
                            blur-on-enter
                            with-seconds="never"
                            label=""
                            placeholder="0:00"
                            required
                            @input="changeDuration(row, $event)"
                            @blur="handleBlur"
                          />
                          <span class="help-block">{{ errors[0] }}</span>
                        </div>
                      </ValidationProvider>
                    </ValidationObserver>
                  </div>
                </AppTableColumn>
              </AppTable>
            </ValidationObserver>
            <div
              v-if="showPagination"
              :class="{ 'pagination-content': true, 'error-class': hasValidationError }"
            >
              <AppPagination
                :key="hasValidationError ? 'has-error' : 'no-error'"
                :resource="{ one: 'item', many: 'items' }"
                :tooltip-title="nextPagePaginationTootip"
                :pagination="pagination"
                @page-changed="changePage"
              />
            </div>
          </div>
        </div>
        <div
          v-show="showDetails && remaningBalanceError"
          class="remaning-balance-error"
        >
          <i class="fa fa-warning text-warning a-tooltip mr-5" />
          <span>This request exceeds the remaining balance. Adjust the date range or hours per day.</span>
        </div>
        <div v-if="!partiallyPaid">
          <ValidationProvider
            v-slot="{ errors }"
            ref="attachmentProvider"
            mode="lazy"
            name="attachment"
          >
            <div :class="{ 'has-error': errors[0] }">
              <AppFileImport
                show-file-types-on-label
                label="Upload file"
                resource-type="client"
                class="upload-file"
                :import-file="false"
                :file-params="attachment"
                :file-types="attachmentSettings.file_types"
                :mime-types="attachmentSettings.mime_types"
                :download-file-url="attachmentUrl"
                :organization-id="organizationId"
                :class="{ 'attachment-loading': loadingAttachment }"
                @upload-file="uploadAttachment"
                @remove-file="removeFile"
              />
              <span
                class="help-block"
                data-testid="upload-error"
              >{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
        <div
          v-if="!partiallyPaid"
          class="mt-20"
        >
          <ValidationProvider
            v-slot="{ errors }"
            mode="aggressive"
            rules="required|max:280"
            name="message"
          >
            <div :class="{ 'has-error': errors[0] }">
              <label class="control-label require">Reason*</label>
              <textarea
                id="sendReport_message"
                v-model="message"
                class="form-control"
              />
              <span class="help-block mb-15">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
        <div class="d-flex align-items-end gap-15 flex-row-reverse">
          <AppButton
            type="primary"
            class="submit-button"
            :disabled="disableSubmitButton"
            @click="timeOffRequestAction"
          >
            {{ sendButtonText }}
          </AppButton>
          <AppButton
            data-testid="time-off-request-setup-cancel-button"
            @click="closeDialog"
          >Cancel</AppButton>
        </div>
      </ValidationObserver>
    </AppDialog>

    <ShiftOverlapConfirmationDialog
      v-if="showShiftOverlapDialog"
      :shift-overlap-message="shiftOverlappingMessage"
      :overlap-shift-count="shiftOverlappingCount"
      :shift-overlap-question="shiftOverlapQuestion"
      :saving="saving"
      @close="closeOverlapConfirmationDialog"
      @open-dialog="openDialog"
      @update-remove-shift-field="handleRemoveShiftField"
    />
  </div>
</template>

<script>
import { TimeOffRequestsResource, TimeOffPoliciesResource, HolidaysResource } from '~/resources';
import TimeOffRequestDialogMixin from '~/mixins/TimeOffRequest/TimeOffRequestDialogMixin';
import TimeOffRequestBalance from '~/components/time_off/TimeOffRequestBalance.vue';
import ShiftOverlapConfirmationDialog from '~/components/ShiftOverlapConfirmationDialog.vue';
import { uploadToS3 } from '~/utils/s3_uploader';

const DEFAULT_AMOUNT_USED_PER_DAY = 28800;

export default {
  name: 'TimeOffRequestSetupDialog',
  components: {
    TimeOffRequestBalance,
    ShiftOverlapConfirmationDialog,
  },
  mixins: [TimeOffRequestDialogMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    selectedTimeOffRequestId: {
      type: Number,
      default: null,
    },
    allowManage: {
      type: Boolean,
      default: false,
    },
    loadDataOnCreation: {
      type: Boolean,
      default: true,
    },
    showOriginalTimeZone: {
      type: Boolean,
      default: false,
    },
    partiallyPaidEdition: {
      type: Boolean,
      default: false,
    },
    currentUserDropdownObject: {
      type: Object,
      required: true,
    },
  },
  data() {
    return this.defaultData();
  },
  computed: {
    statusSymClass() {
      if (this.partiallyPaid && this.statusSym) {
        return 'partial-paid';
      }

      return this.statusSym;
    },
    resource() {
      return TimeOffRequestsResource.members;
    },
    nextPagePaginationTootip() {
      if (!this.hasValidationError) {
        return null;
      }

      return 'Fix the errors highlighted in the details to change the page';
    },
    disableSubmitButton() {
      return this.loadingBalance || this.loadingInitial || this.loadingAttachment || this.saving;
    },
    nextPageDateTooltip() {
      if (this.hasValidationError) {
        return { placement: 'bottom', title: 'Fix the errors highlighted in the details to change the date' };
      }

      return null;
    },
    hasAttachment() {
      return Object.keys(this.attachment).length !== 0;
    },
    getOneYearsFromNow() {
      return moment().add(1, 'years').endOf('year');
    },
    timeOffPoliciesListData() {
      return this.timeOffPolicies.map((policies) => ({
        id: policies.id,
        text: policies.text,
      }));
    },
    policyRequiresApproval() {
      const object = {};

      for (const policy of this.timeOffPolicies) {
        object[policy.id] = policy.requires_approval;
      }

      return object;
    },
    findSelectedMember() {
      return this.members.find((member) => Number(member.id) === Number(this.selectedMember));
    },
    showUserField() {
      return this.allowManage && !this.timeOffRequestId;
    },
    sendButtonText() {
      return this.timeOffRequestId ? 'Submit update' : 'Send';
    },
    userBalanceAvatar() {
      if (this.loadingInitial) {
        return '';
      }

      return this.selectedMemberObject?.avatar;
    },
    userBalanceName() {
      if (this.loadingInitial) {
        return '';
      }

      return this.selectedMemberObject?.name;
    },
  },
  async created() {
    Object.assign(this.$data, this.defaultData());

    if (this.loadDataOnCreation) {
      if (this.selectedTimeOffRequestId) {
        this.timeOffRequestId = this.selectedTimeOffRequestId;
        await this.fetchTimeOffRequest(this.timeOffRequestId);
      } else {
        await this.fetchInitialData();
      }
    }
  },
  methods: {
    defaultData() {
      return {
        showDialog: this.show,
        timeOffRequestDays: [],
        pagination: {
          current_page: 1,
          total_pages: 1,
          next_page: null,
          prev_page: null,
          limit_value: 7,
          current_count: 0,
          total_count: 0,
          first_page: true,
          last_page: true,
        },
        paginatedItems: [],
        loadingBalance: false,
        loadingInitial: false,
        members: [],
        timeOffPolicies: [],
        holidays: [],
        currentBalance: '0:00',
        hoursUsed: '0:00',
        remainingBalance: '0:00',
        pendingApproval: '0:00',
        showNextYearBalance: false,
        currentBalanceNextYear: '0:00',
        holidayHours: '0:00',
        hoursUsedNextYear: '0:00',
        remainingBalanceNextYear: '0:00',
        pendingApprovalNextYear: '0:00',
        holidayHoursNextYear: '0:00',
        totalAmountUsed: '0:00',
        currentPage: 1,
        selectedMember: null,
        selectedMemberObject: this.currentUserDropdownObject,
        selectedPolicy: null,
        selectedPolicyName: '',
        startsAt: moment(),
        stopsAt: moment(),
        allDay: true,
        excludeWeekends: true,
        excludeHolidays: true,
        showDetails: true,
        message: '',
        negativeBalanceErrorMessage: 'Selected policy does not allow negative balances',
        startAtYear: '',
        stopAtYear: '',
        showPagination: false,
        hasValidationError: false,
        remaningBalanceError: false,
        startTime: moment().set({ hour: 0, minute: 0, second: 0 }),
        stopTime: moment().set({ hour: 23, minute: 59, second: 59 }),
        startTimeWithoutAllDay: moment().set({ hour: 9, minute: 0, second: 0 }),
        stopTimeWithoutAllDay: moment().set({ hour: 17, minute: 0, second: 0 }),
        uniqueKey: Date.now(),
        policyType: '',
        shiftOverlappingCount: 0,
        shiftOverlappingMessage: '',
        shiftOverlapQuestion: '',
        showShiftOverlapDialog: false,
        removeShifts: false,
        saving: false,
        paidDaysMessage: '',
        timeOffRequestId: null,
        tzMessage: null,
        showTzMessage: false,
        originalTimeZone: null,
        attachment: {},
        attachmentUrl: null,
        attachmentSettings: {
          presign_path: '',
          file_types: '',
          mime_types: '',
        },
        loadingAttachment: false,
        attachmentAltered: false,
        requestedTimeOffMessage: '',
        responseForDeny: '',
        partiallyPaid: false,
        statusSym: '',
        status: '',
        totalPaidHours: 0,
        url: '',
      };
    },
    closeOverlapConfirmationDialog(value) {
      this.showShiftOverlapDialog = false;

      if (value === 'back-to-previous-dialog') {
        this.showDialog = true;
      } else {
        this.closeDialog();
      }
    },
    openDialog() {
      this.showShiftOverlapDialog = false;
      this.showDialog = true;
    },
    handleRemoveShiftField(_, removeShifts) {
      this.removeShifts = removeShifts;
      this.createTimeOffRequest();
    },
    async uploadAttachment(file) {
      this.attachmentUrl = null;

      try {
        return await new Promise((resolve, reject) => {
          this.loadingAttachment = true;
          uploadToS3(this.attachmentSettings.presign_path, file, resolve, reject);
        }).then((response) => {
          this.attachment = {
            id: response.fields.key.match(/cache\/(.+)$/)[1],
            storage: 'cache',
            metadata: {
              size: file.size,
              filename: file.name,
              mime_type: file.type,
            },
          };
          this.loadingAttachment = false;
          this.attachmentAltered = true;
        });
      } catch (e) {
        const responseXML = e.responseXML;

        this.loadingAttachment = false;
        if (responseXML && responseXML.documentElement.firstChild.innerHTML === 'EntityTooLarge') {
          const errorMessage = `File is too large. (max is ${this.attachmentSettings.max_size} MB)`;

          this.$refs.attachmentProvider.setErrors([errorMessage]);
          throw new Error(errorMessage);
        } else {
          const errorMessage = 'Unexpected error. Please try again.';

          this.$refs.attachmentProvider.setErrors([errorMessage]);
          throw new Error(errorMessage);
        }
      }
    },
    removeFile() {
      this.attachmentUrl = null;
      this.attachment = {};
      this.attachmentAltered = true;
      this.$refs.attachmentProvider.reset();
    },
    hoursPerDayTooltipText(row) {
      if (this.excludeWeekends && row.weekend) {
        return 'Weekends are excluded from selection. Uncheck "Exclude weekends" checkbox to add hours.';
      }

      const isHoliday = this.holidays.find((holiday) => holiday.date === row.date);

      if (this.excludeHolidays && !!isHoliday) {
        return 'Holidays are excluded from selection. Uncheck "Exclude holidays" checkbox to add hours.';
      }

      return '';
    },
    defaultAmountUsedValueByDay(date, holiday) {
      if (holiday && holiday.amount_used !== 0) {
        return 0;
      }

      return DEFAULT_AMOUNT_USED_PER_DAY;
    },
    weekendDay(date) {
      return date.day() === 0 || date.day() === 6;
    },
    async updateDates(newDate, type) {
      if (type === 'startsAt') {
        this.startsAt = newDate;
        this.updateTime(
          this.startTimeWithoutAllDay.clone().set({ year: newDate.year(), month: newDate.month(), date: newDate.date() }),
          'startTime'
        );
      } else {
        this.stopsAt = newDate;
        this.updateTime(
          this.stopTimeWithoutAllDay.clone().set({ year: newDate.year(), month: newDate.month(), date: newDate.date() }),
          'stopTime'
        );
      }

      if (this.startsAt > this.stopsAt) {
        await this.$nextTick();

        return this.updateDates(this.startsAt, 'stopsAt');
      }

      await this.fetchHolidays();
      await this.buildTimeOffRequestDays();
      await this.fetchUserBalance();
    },
    resetBalance() {
      this.showNextYearBalance = false;
      this.currentBalance = '0:00';
      this.hoursUsed = '0:00';
      this.remainingBalance = '0:00';
      this.pendingApproval = '0:00';
      this.holidayHours = '0:00';
      this.currentBalanceNextYear = '0:00';
      this.hoursUsedNextYear = '0:00';
      this.remainingBalanceNextYear = '0:00';
      this.pendingApprovalNextYear = '0:00';
      this.holidayHoursNextYear = '0:00';
    },
    async fetchInitialData() {
      try {
        this.loadingInitial = true;
        this.loadingBalance = true;
        const params = { organizationId: this.organizationId };
        const response = await TimeOffRequestsResource.new(params, null, { abortReload: true });

        if (response) {
          this.selectedMember = this.currentUserDropdownObject.id;
          this.attachmentSettings = response.attachment_settings;
          await this.fetchHolidays();
          await this.fetchPolicy();
          await this.buildTimeOffRequestDays();
          await this.fetchUserBalance();

          this.loadingInitial = false;
          this.loadingBalance = false;
        } else {
          throw new Error('Unable to fetch data for the time off request creation.');
        }
      } catch (error) {
        this.closeDialog();
        await this.$appMessage('Unable to fetch data for the time off request creation. Try again later.');
      }
    },
    async fetchTimeOffRequest(timeOffRequestId, partiallyPaidEdition = false) {
      try {
        this.partiallyPaid = partiallyPaidEdition || this.partiallyPaidEdition;
        this.loadingInitial = true;
        this.loadingBalance = true;

        const params = { id: timeOffRequestId };
        const response = await TimeOffRequestsResource.edit(params, null, { abortReload: true });

        if (response) {
          this.members = [response.user];
          this.selectedMember = response.user.id;
          this.timeOffPolicies = response.time_off_policies.map((policy) => {
            return {
              id: policy.id,
              text: policy.name,
              paid: policy.paid,
            };
          });
          const policyExists = this.timeOffPolicies.some((policy) => policy.id === response.time_off_policy.id);

          if (!policyExists) {
            this.timeOffPolicies.push({
              id: response.time_off_policy.id,
              text: response.time_off_policy.name,
              paid: response.time_off_policy.paid,
            });
          }
          this.selectedPolicy = response.time_off_policy.id;
          this.selectedPolicyName = response.time_off_policy.name;
          this.selectedMemberObject = response.time_off_request_user_dropdown_object;
          this.requestedTimeOffMessage = response.time_off_requested_message;
          this.message = response.message;
          this.responseForDeny = response.response;
          this.policyType = response.time_off_policy.paid ? 'paid' : 'unpaid';
          this.startsAt = moment.tz(response.starts_at, response.original_time_zone);
          this.stopsAt = moment.tz(response.stops_at, response.original_time_zone);
          this.allDay = response.all_day;
          this.tzMessage = response.tz_message;
          this.paidDaysMessage = response.paid_days_message;
          this.showTzMessage = response.original_time_zone !== response.user.time_zone.for;
          this.statusSym = response.status_sym;
          this.status = response.status_name;
          this.originalTimeZone = response.original_time_zone;
          this.totalPaidHours = response.time_off_request_days_paid_hours;
          this.partiallyPaid = response.partially_paid;

          this.updateTime(this.startsAt.clone(), 'startTime');
          this.updateTime(this.stopsAt.clone(), 'stopTime');

          this.attachment = response.attachment || {};
          this.attachmentUrl = response.attachment_original_url || '';
          this.attachmentSettings = response.attachment_settings;
          this.excludeWeekends = response.exclude_weekends;
          this.excludeHolidays = response.exclude_holidays;
          this.holidays = this.processHolidays(response.holidays);
          this.timeOffRequestDays = response.time_off_request_days;
          this.fetchedBalanceInitialInfo(response.balances);
          this.calculatePaginationItems();
          await this.fetchShiftOverlaping();
          this.loadingInitial = false;
          this.loadingBalance = false;
        } else {
          throw new Error('Unable to fetch data for the time off request edition.');
        }
      } catch (error) {
        this.closeDialog();
        await this.$appMessage('Unable to fetch data for the time off request edition. Try again later.');
      }
    },
    async fetchHolidays() {
      try {
        const params = {
          organizationId: this.organizationId,
          userId: this.selectedMember,
          startsAtDate: this.startsAt.format('YYYY-MM-DD'),
          stopsAtDate: this.stopsAt.format('YYYY-MM-DD'),
        };
        const response = await HolidaysResource.user_holidays(params, null, { abortReload: true });

        this.holidays = this.processHolidays(response);
      } catch (error) {
        this.closeDialog();
        this.$appMessage('Unexpected error. Please try again.');
      }
    },
    async fetchPolicy() {
      try {
        const params = { userId: this.selectedMember, organizationId: this.organizationId };
        const response = await TimeOffPoliciesResource.user_policies(params, null, { abortReload: true });

        this.timeOffPolicies = response.results;

        if (response.results.length > 0) {
          const policyNotFound =
            this.selectedPolicy &&
            !response.results.find((policy) => Number(policy.id) === Number(this.selectedPolicy));

          if (policyNotFound || !this.selectedPolicy) {
            this.selectedPolicy = response.results[0].id;
            this.selectedPolicyName = response.results[0].text;
            this.policyType = response.results[0].paid ? 'paid' : 'unpaid';
          }
        } else {
          this.selectedPolicy = null;
          this.selectedPolicyName = '';
          this.policyType = '';
          await this.$nextTick();
          this.resetBalance();
        }
      } catch (error) {
        this.closeDialog();
        this.$appMessage('Unexpected error. Please try again.');
      }
    },
    async fetchUserBalance() {
      try {
        if (!this.selectedMember || !this.selectedPolicy) {
          this.$refs.policyObserver.reset();
          this.$refs.policyObserver.errors = {};
          this.remaningBalanceError = false;

          this.resetBalance();
          this.hoursUsed = this.formatBalanceInformation(this.totalAmountUsed);
          this.remainingBalance = this.formatBalanceInformation(this.totalAmountUsed * -1);
          this.holidayHours = this.formatBalanceInformation(
            this.holidays.reduce((acc, holiday) => acc + holiday.amount_used, 0),
          );
          this.startAtYear = '';
          this.stopsAtYear = '';

          return;
        }

        if (!this.loadingInitial) {
          this.loadingBalance = true;
        }

        const params = {
          id: this.timeOffRequestId ? this.timeOffRequestId : null,
          amountUsed: String(this.totalAmountUsed),
          organizationId: this.organizationId,
          timeOffPolicyId: this.selectedPolicy,
          userId: this.selectedMember,
          startsAtDate: this.startsAt.format('YYYY-MM-DD'),
          stopsAtDate: this.stopsAt.format('YYYY-MM-DD'),
          firstYearAmount: String(
            this.timeOffRequestDays
              .filter((day) => parseInt(day.date.substring(0, 4)) === this.startsAt.year())
              .reduce((acc, day) => acc + day.amount_used, 0) || 0,
          ),
          secondYearAmount: String(
            this.timeOffRequestDays
              .filter((day) => parseInt(day.date.substring(0, 4)) === this.startsAt.year() + 1)
              .reduce((acc, day) => acc + day.amount_used, 0) || 0,
          ),
        };
        const response = await TimeOffRequestsResource.user_balance(params, null, { abortReload: true });

        if (response) {
          this.fetchedBalanceInitialInfo(response);
          await this.fetchShiftOverlaping();

          if (!this.loadingInitial) {
            this.loadingBalance = false;
          }
        } else {
          throw new Error('Unable to fetch data for the time off request creation.');
        }
      } catch (error) {
        this.closeDialog();
        this.$appMessage('Unexpected error. Please try again.');
      }
    },
    async fetchShiftOverlaping() {
      try {
        if (!this.selectedMember) {
          return;
        }

        const params = {
          organizationId: this.organizationId,
        };
        const data = {
          user_ids: [this.selectedMember],
          start_date: this.startsAt.format('YYYY-MM-DD'),
          stop_date: this.stopsAt.format('YYYY-MM-DD'),
        };

        if (!this.allDay) {
          data.start_time = this.startTime.format('HH:mm:ss');
          data.stop_time = this.stopTime.format('HH:mm:ss');
          data.timezone = this.originalTimeZone || this.selectedMemberObject.time_zone;
        }

        const response = await TimeOffRequestsResource.shiftOverlaps(params, data, { abortReload: true });

        this.shiftOverlappingCount = response.shift_count;
        this.shiftOverlappingMessage = response.shift_count > 0 ? response.warning : '';
        this.shiftOverlapQuestion = response.shift_count > 0 ? response.question : '';
      } catch (error) {
        this.shiftOverlappingCount = 0;
        this.shiftOverlappingMessage = '';
        this.shiftOverlapQuestion = '';
      }
    },
    async timeOffRequestAction() {
      if (this.timeOffRequestId) {
        await this.updateTimeOffRequest();
      } else {
        if (this.shiftOverlappingCount > 0 && this.policyRequiresApproval[this.selectedPolicy] === false) {
          this.closeDialog(false);
          this.showShiftOverlapDialog = true;

          return;
        }

        await this.createTimeOffRequest();
      }
    },
    async createTimeOffRequest() {
      try {
        this.saving = true;

        const policyErrors = this.$refs.policyObserver?.errors;
        const valid = await this.$refs.dialogObserver.validate();
        const policyErrorsIsEmpty = policyErrors?.policy && policyErrors.policy.length === 0;

        if (policyErrorsIsEmpty === false) {
          this.$refs.policyObserver.setErrors(policyErrors);

          return;
        }

        if (!valid || this.timeOffRequestId) {
          return;
        }

        const params = {
          organizationId: this.organizationId,
        };
        const payload = {
          time_off_request: {
            user_id: this.selectedMember,
            time_off_policy_id: this.selectedPolicy,
            starts_at_date: this.startsAt.format('YYYY-MM-DD'),
            stops_at_date: this.stopsAt.format('YYYY-MM-DD'),
            starts_at_time: this.startTime.format('HH:mm:ss'),
            stops_at_time: this.stopTime.format('HH:mm:ss'),
            all_day: this.allDay,
            amount_used: String(this.totalAmountUsed),
            message: this.message,
            exclude_holidays: this.excludeHolidays,
            exclude_weekends: this.excludeWeekends,
            remove_shifts: this.removeShifts,
          },
          time_off_request_days: this.timeOffRequestDays.map((day) => ({
            date: day.date,
            amount_used: day.amount_used,
          })),
          redirect_path: this.redirectPath,
          current_page_title: document.title,
          current_url: document.URL,
        };

        if (this.attachment.id) {
          payload.time_off_request.attachment = this.attachment;
        }

        const response = await TimeOffRequestsResource.create(params, payload, { abortReload: true });

        if (response.success) {
          this.closeDialog();
          this.showShiftOverlapDialog = false;
          window.location.assign(response.url);
        } else if (response.errors) {
          const filteredErrors = {};

          Object.entries(response.errors).forEach(([key, errors]) => {
            filteredErrors[key] = errors.filter((error) => !error.startsWith('custom-allocation-'));
          });

          this.$refs.dialogObserver.setErrors(filteredErrors);
          await this.$appMessage('The time off request cannot be created.');
        } else {
          throw new Error('Unable to create time off request.');
        }
      } catch (error) {
        await this.$appMessage('Unable to create time off request. Try again later.');
      } finally {
        this.saving = false;
      }
    },
    async updateTimeOffRequest() {
      try {
        this.saving = true;

        const policyErrors = this.$refs.policyObserver?.errors;
        const valid = await this.$refs.dialogObserver.validate();
        const policyErrorsIsEmpty = policyErrors?.policy && policyErrors.policy.length === 0;

        if (policyErrorsIsEmpty === false) {
          this.$refs.policyObserver.setErrors(policyErrors);

          return;
        }

        if (!valid || !this.timeOffRequestId) {
          return;
        }

        const params = {
          id: this.timeOffRequestId,
        };
        const payload = {
          time_off_request: {
            id: this.timeOffRequestId,
            user_id: this.selectedMember,
            time_off_policy_id: this.selectedPolicy,
            starts_at_date: this.startsAt.format('YYYY-MM-DD'),
            stops_at_date: this.stopsAt.format('YYYY-MM-DD'),
            starts_at_time: this.startTime.format('HH:mm:ss'),
            stops_at_time: this.stopTime.format('HH:mm:ss'),
            all_day: this.allDay,
            amount_used: String(this.totalAmountUsed),
            message: this.message,
            exclude_holidays: this.excludeHolidays,
            exclude_weekends: this.excludeWeekends,
          },
          time_off_request_days: this.timeOffRequestDays.map((day) => ({
            date: day.date,
            amount_used: day.amount_used,
          })),
          redirect_path: this.redirectPath || location.pathname + location.search,
          current_page_title: document.title,
          current_url: document.URL,
        };

        if (this.attachmentAltered) {
          payload.time_off_request.attachment = this.attachment;
        }

        const response = await TimeOffRequestsResource.update(params, payload, { abortReload: true });

        if (response.success) {
          this.closeDialog();
          window.location.assign(response.url);
        } else if (response.errors) {
          const filteredErrors = {};

          Object.entries(response.errors).forEach(([key, errors]) => {
            filteredErrors[key] = errors.filter((error) => !error.startsWith('custom-allocation-'));
          });

          this.$refs.dialogObserver.setErrors(filteredErrors);
          await this.$appMessage('The time off request cannot be updated.');
        } else {
          throw new Error('Unable to update time off request.');
        }
      } catch (error) {
        await this.$appMessage('Unable to update time off request. Try again later.');
      } finally {
        this.saving = false;
      }
    },
    async loadInitialData(timeOffRequestId = null, partiallyPaidEdition = false, url = null) {
      Object.assign(this.$data, this.defaultData());

      this.showDialog = true;
      this.url = url;

      if (timeOffRequestId) {
        this.timeOffRequestId = timeOffRequestId;
        await this.fetchTimeOffRequest(timeOffRequestId, partiallyPaidEdition);
      } else {
        await this.fetchInitialData();
      }
    },
    async buildTimeOffRequestDays(updateDaysOnly = false) {
      const startsAt = moment(this.startsAt.format('YYYY-MM-DD')).startOf('day');
      const stopsAt = moment(this.stopsAt.format('YYYY-MM-DD')).endOf('day');

      this.timeOffRequestDays = this.timeOffRequestDays.filter((day) => {
        const dayDate = moment(day.date).startOf('day');

        return dayDate.isSameOrAfter(startsAt, 'day') && dayDate.isSameOrBefore(stopsAt, 'day');
      });

      const existingDates = new Set(this.timeOffRequestDays.map((day) => day.date));

      for (let day = startsAt; day.isSameOrBefore(stopsAt, 'day'); day.add(1, 'days').startOf('day')) {
        const dateStr = day.format('YYYY-MM-DD');
        const findHoliday = this.holidays.find((holiday) => String(holiday.date) === dateStr);

        if (!existingDates.has(dateStr)) {
          const disabled = !!(
            (this.excludeWeekends && this.weekendDay(day)) ||
            (this.excludeHolidays && findHoliday && findHoliday.amount_used !== 0)
          );
          const newDay = {
            date: dateStr,
            amount_used: disabled ? 0 : this.defaultAmountUsedValueByDay(day, findHoliday),
            weekend: this.weekendDay(day),
            holiday: !!findHoliday,
            disabled,
          };
          const insertAt = this.timeOffRequestDays.findIndex((existingDay) => moment(existingDay.date).isAfter(day));

          if (insertAt === -1) {
            this.timeOffRequestDays.push(newDay);
          } else {
            this.timeOffRequestDays.splice(insertAt, 0, newDay);
          }

          existingDates.add(dateStr);
        } else {
          const existingDay = this.timeOffRequestDays.find((day) => day.date === dateStr);
          const disabled = !!(
            (this.excludeWeekends && this.weekendDay(day)) ||
            (this.excludeHolidays && findHoliday)
          );

          if (existingDay) {
            const prevAmountUsed = existingDay.disabled !== disabled ?
              this.defaultAmountUsedValueByDay(day, findHoliday) : existingDay.amount_used;

            existingDay.holiday = !!findHoliday;
            existingDay.weekend = this.weekendDay(day);
            existingDay.disabled = disabled;
            existingDay.amount_used = disabled ? 0 : prevAmountUsed;
          }
        }
      }

      if (updateDaysOnly) {
        return;
      }

      this.calculatePaginationItems();
      this.calculateAmountUsed();
    },
    calculateAmountUsed() {
      let totalAmountUsed = 0;

      this.timeOffRequestDays.forEach((day) => {
        if (!day.disabled) {
          totalAmountUsed += day.amount_used;
        }
      });

      if (this.partiallyPaid) {
        this.totalAmountUsed = totalAmountUsed + this.totalPaidHours;
      } else {
        this.totalAmountUsed = totalAmountUsed;
      }
    },
    async changeDuration(row, value) {
      if (value === row.amount_used || row.disabled) {
        return;
      }

      row.amount_used = value;

      this.calculateAmountUsed();
      await this.fetchUserBalance();
    },
    async changeTime(oldValue, value, timeType) {
      this.updateTime(
        oldValue.clone().hour(value.hour()).minute(value.minute()).second(0),
        timeType
      );

      await this.fetchShiftOverlaping();
    },
    updateTime(newDate, updatedField) {
      this[`${updatedField}WithoutAllDay`] = newDate.clone();

      if (this.startTimeWithoutAllDay.isSame(this.stopTimeWithoutAllDay, 'day')) {
        if (updatedField === 'startTime' && this.startTimeWithoutAllDay.isAfter(this.stopTimeWithoutAllDay)) {
          this.stopTimeWithoutAllDay = this.startTimeWithoutAllDay.clone().add(1, 'hours');
        }
        if (updatedField === 'stopTime' && this.stopTimeWithoutAllDay.isBefore(this.startTimeWithoutAllDay)) {
          this.startTimeWithoutAllDay = this.stopTimeWithoutAllDay.clone().subtract(1, 'hours');
        }
      }

      if (!this.allDay) {
        this.startTime = this.startTimeWithoutAllDay.clone();
        this.stopTime = this.stopTimeWithoutAllDay.clone();
      }
    },
    async updateDisabledDays() {
      this.timeOffRequestDays.forEach((day) => {
        const isHoliday = this.holidays.some((holiday) => holiday.date === day.date);
        const shouldDisable =
          (this.excludeWeekends && day.weekend && !isHoliday) || (this.excludeHolidays && isHoliday && !day.weekend);

        day.disabled = shouldDisable;
        day.amount_used = shouldDisable ? 0 : DEFAULT_AMOUNT_USED_PER_DAY;
      });

      this.calculateAmountUsed();
      await this.fetchUserBalance();
    },
    async excludeWeekendsChanged() {
      await this.buildTimeOffRequestDays(true);
      this.calculateAmountUsed();
      await this.fetchUserBalance();
    },
    async excludeHolidaysChanged() {
      await this.buildTimeOffRequestDays(true);
      this.calculateAmountUsed();
      await this.fetchUserBalance();
    },
    async memberChanged(user) {
      if (user) {
        this.selectedMember = user.id;
        this.selectedMemberObject = user;
        await this.fetchHolidays();
        await this.fetchPolicy();
        await this.buildTimeOffRequestDays();
        await this.fetchUserBalance();
      }
    },
    async policyChanged() {
      this.policyType = this.timeOffPolicies.find((policy) => Number(policy.id) === Number(this.selectedPolicy)).paid
        ? 'paid'
        : 'unpaid';
      await this.buildTimeOffRequestDays();
      await this.fetchUserBalance();
    },
    checkAndAddNegativeBalanceError(balanceAmountLeft, balanceAmountLeftNextYear, allowNegativeBalance) {
      const policyObserver = this.$refs.policyObserver;

      if (allowNegativeBalance === true) {
        this.$refs.policyObserver.reset();
        this.$refs.policyObserver.errors = {};
        this.remaningBalanceError = false;
      }

      if ((balanceAmountLeft < 0 || balanceAmountLeftNextYear < 0) && allowNegativeBalance === false) {
        const negativeBalanceErrorIsPresent = policyObserver.errors?.policy?.includes(this.negativeBalanceErrorMessage);

        this.remaningBalanceError = true;

        if (!negativeBalanceErrorIsPresent) {
          policyObserver.setErrors({ policy: this.negativeBalanceErrorMessage });
        }
      } else {
        this.$refs.policyObserver.reset();
        this.$refs.policyObserver.errors = {};
        this.remaningBalanceError = false;
      }
    },
    async changePage(pageNumber) {
      if (this.hasValidationError) {
        return;
      }

      this.calculatePaginationItems(pageNumber);
      this.uniqueKey = Date.now();
    },
    closeDialog(emitCloseEvent = true) {
      this.showDialog = false;
      if (this.$refs.dialogObserver) {
        this.$refs.dialogObserver.reset();
      }
      if (emitCloseEvent) {
        this.$emit('close');
      }
    },
    async handleBlur() {
      await this.$nextTick();
      const isValid = await this.$refs.tableObserver.validate();

      if (!isValid) {
        this.hasValidationError = true;
      } else {
        this.hasValidationError = false;
      }
    },
    async updateAllDay() {
      if (this.allDay) {
        this.startTime = this.startTime.set({ hour: 0, minute: 0, second: 0 });
        this.stopTime = this.stopTime.set({ hour: 23, minute: 59, second: 59 });
      } else {
        this.startTime = this.startTimeWithoutAllDay.clone();
        this.stopTime = this.stopTimeWithoutAllDay.clone();
      }

      await this.fetchShiftOverlaping();
    },
    fetchedBalanceInitialInfo(response) {
      this.showNextYearBalance = false;
      this.checkAndAddNegativeBalanceError(
        response.amount_left.current,
        response.amount_left.next,
        response.allow_negative_balances,
      );
      this.currentBalance = this.formatBalanceInformation(response.starting_balance.current);
      this.hoursUsed = this.formatBalanceInformation(response.amount_used.current);
      this.remainingBalance = this.formatBalanceInformation(response.amount_left.current);
      this.pendingApproval = this.formatBalanceInformation(response.pending_approval.current);
      this.holidayHours = this.formatBalanceInformation(response.holiday_hours.current);
      this.selectedPolicyName =
        this.timeOffPolicies.find((policy) => Number(policy.id) === Number(this.selectedPolicy))?.text || '';
      this.startAtYear = this.startsAt.format('YYYY');
      this.stopAtYear = this.stopsAt.format('YYYY');

      if (response.starting_balance.next !== undefined) {
        this.showNextYearBalance = true;
        this.checkAndAddNegativeBalanceError(
          response.amount_left.current,
          response.amount_left.next,
          response.allow_negative_balances,
        );
        this.currentBalanceNextYear = this.formatBalanceInformation(response.starting_balance.next);
        this.hoursUsedNextYear = this.formatBalanceInformation(response.amount_used.next);
        this.remainingBalanceNextYear = this.formatBalanceInformation(response.amount_left.next);
        this.pendingApprovalNextYear = this.formatBalanceInformation(response.pending_approval.next);
        this.holidayHoursNextYear = this.formatBalanceInformation(response.holiday_hours.next);
      }
    },
    fetcher(payload) {
      return TimeOffRequestsResource.members_dropdown({ organization_id: this.organizationId }, payload, {
        abortReload: true,
      });
    },
  },
};
</script>
